import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';

import { getCompanyFinancialsByTicker } from '~/database/supabase/financials/queries.server.ts';
import { getHints } from '~/utils/client-hints.tsx';
import { getFinancialsData } from '~/views/instrument/utils/format-financial-data.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

const GetInstrumentFinancialsPageDataSchema = z.object({
	ticker: z.string().optional(),
});

type InstrumentFinancialsResult = NonNullable<ReturnType<typeof getFinancialsData>>;

export interface InstrumentFinancialsPageData {
	readonly formattedFinancials: InstrumentFinancialsResult['formattedFinancials'] | null;
	readonly formatting: InstrumentFinancialsResult['formatting'] | null;
	readonly userLocale: string;
}

export const getInstrumentFinancialsPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: ['auth.session', 'supabase.financials'],
			relatesTo: 'instrument.financials',
		}),
	])
	.inputValidator(GetInstrumentFinancialsPageDataSchema)
	.handler(async ({ data }) => {
		const request = getRequest();
		await requireStartAuthSession({ request });
		const userLocale = getHints(request).locale;
		const ticker = data.ticker;

		if (!ticker) {
			return {
				formattedFinancials: null,
				formatting: null,
				userLocale,
			} satisfies InstrumentFinancialsPageData;
		}

		const financials = await getCompanyFinancialsByTicker({ ticker });
		const financialsData = getFinancialsData(financials);

		return {
			formattedFinancials: financialsData?.formattedFinancials ?? null,
			formatting: financialsData?.formatting ?? null,
			userLocale,
		} satisfies InstrumentFinancialsPageData;
	});
